<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { Loader2, MoreHorizontal, Trash2, ImageIcon, Download, BookOpen, ExternalLink } from 'lucide-vue-next'
  import { useReportThemesStore } from '@stores/reportThemes/reportThemes'
  import { useCurrentUserStore } from '@stores/currentUser'
  import { useAccountsStore } from '@stores/accounts'
  import { IndexPageLayout, IndexPageFilters } from '@/components/App/IndexPage'
  import { Card, CardContent } from '@/components/ui/card'
  import { EmptyState } from '@/components/episto-ui/empty-state'
  import { LoadingState } from '@/components/episto-ui/loading-state'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import { Label } from '@/components/ui/label'
  import { Select } from "@/components/episto-ui/select";
  import { FileDropzone } from '@/components/episto-ui/file-dropzone'
  import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  } from '@/components/ui/dialog'
  import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  } from '@/components/ui/dropdown-menu'
  import { ConfirmDialog } from '@/components/episto-ui/confirm-dialog'
  import { useConfirmDelete } from '@/utils/composables/useConfirmDelete'
  import type { ReportThemeListItem } from '@/types'

  const router = useRouter()
  const store = useReportThemesStore()
  const { themes, loading } = storeToRefs(store)

  const { isAdmin } = storeToRefs(useCurrentUserStore())
  const accountsStore = useAccountsStore()
  const accountOptions = computed(() =>
    (accountsStore.accounts ?? []).map((a) => ({ value: String(a.id), label: a.name })),
  )
  // Single-account clients skip the picker (their theme is auto-attached to it).
  // Admins must pick a target account; multi-account users choose among theirs.
  const showAccountSelect = computed(() => isAdmin.value || accountOptions.value.length > 1)

  const searchQuery = ref('')

  const filteredThemes = computed(() => {
    if (!searchQuery.value.trim()) return themes.value
    const query = searchQuery.value.toLowerCase()
    return themes.value.filter((theme) => theme.name.toLowerCase().includes(query))
  })

  // ── Create modal ───────────────────────────────────────────────────────
  // Step-by-step guide to build a theme PPTX (download → customize → upload).
  const DOC_URL = 'https://episto.slite.page/p/TLK10-JSpYjT1m/Tuto-Creer-son-theme-de-rapport-PPT'

  const showCreateModal = ref(false)
  const createName = ref('')
  const createFile = ref<File | null>(null)
  const selectedAccountIds = ref<string[]>([])
  const creating = ref(false)
  const createError = ref<string | null>(null)

  function resetCreate(open: boolean) {
    if (!open) {
      createName.value = ''
      createFile.value = null
      selectedAccountIds.value = []
      createError.value = null
    }
  }

  function openDownloadTemplate() {
    window.open('/api/report_themes/download_template', '_blank')
  }

  function openGuide() {
    window.open(DOC_URL, '_blank', 'noopener,noreferrer')
  }

  async function handleCreate() {
    const file = createFile.value
    if (!createName.value.trim() || !file) return
    creating.value = true
    createError.value = null
    try {
      const accountIds = selectedAccountIds.value.map(Number)
      const theme = await store.createTheme({
        name: createName.value.trim(),
        ...(accountIds.length ? { account_ids: accountIds } : {}),
      })
      await store.uploadPptx(theme.token, file)
      showCreateModal.value = false
      router.push({ name: 'ReportThemeDetail', params: { themeToken: theme.token } })
    } catch (e) {
      createError.value = e instanceof Error ? e.message : 'Failed to create theme'
    } finally {
      creating.value = false
    }
  }

  const {
    isOpen: showDeleteDialog,
    target: themeToDelete,
    requestDelete: confirmDelete,
    confirm: handleDelete,
  } = useConfirmDelete<ReportThemeListItem>(async (theme) => {
    await store.deleteTheme(theme.token)
  })

  onMounted(() => {
    store.fetchThemes()
    accountsStore.fetch()
  })
</script>

<template>
  <IndexPageLayout
    title="Report Themes"
    action-label="New theme"
    @action="showCreateModal = true">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search report themes" />
    </template>

    <LoadingState v-if="loading" />

    <EmptyState
      v-else-if="filteredThemes.length === 0"
      :title="themes.length === 0 ? 'Create your first report theme.' : 'No report themes match your search'" />

    <div
      v-else
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      <Card
        v-for="theme in filteredThemes"
        :key="theme.id"
        class="cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
        @click="router.push({ name: 'ReportThemeDetail', params: { themeToken: theme.token } })">
        <div class="aspect-video bg-muted overflow-hidden">
          <img
            v-if="theme.cover_preview_base64"
            :src="`data:image/png;base64,${theme.cover_preview_base64}`"
            :alt="theme.name"
            class="w-full h-full object-cover" />
          <div
            v-else
            class="w-full h-full flex items-center justify-center">
            <ImageIcon class="w-8 h-8 text-muted-foreground/30" />
          </div>
        </div>
        <CardContent class="p-3">
          <div class="flex items-center justify-between gap-2">
            <p class="font-medium text-sm truncate">{{ theme.name }}</p>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button
                  variant="ghost"
                  size="icon"
                  class="shrink-0 h-7 w-7"
                  @click.stop>
                  <MoreHorizontal class="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem @click.stop="confirmDelete(theme)">
                  <Trash2 class="w-4 h-4 mr-2 text-destructive" />
                  <span class="text-destructive">Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    </div>

    <template #dialogs>
      <!-- Create modal -->
      <Dialog
        v-model:open="showCreateModal"
        @update:open="resetCreate">
        <DialogContent class="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New report theme</DialogTitle>
            <DialogDescription>
              Create a theme from a customized PowerPoint file.
            </DialogDescription>
          </DialogHeader>

          <div class="space-y-5 py-2">
            <div class="space-y-1.5">
              <Label>Theme name</Label>
              <Input
                v-model="createName"
                placeholder="My theme"
                @keydown.enter="handleCreate" />
            </div>

            <div
              v-if="showAccountSelect"
              class="space-y-1.5">
              <Label>Accounts</Label>
              <Select
                v-model="selectedAccountIds"
                :options="accountOptions"
                multiple
                filterable
                placeholder="Select accounts" />
            </div>

            <!-- Step 1 -->
            <div class="space-y-1.5">
              <Label>1. Download the PowerPoint template</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                @click="openDownloadTemplate">
                <Download class="w-4 h-4 mr-1.5" />
                Download
              </Button>
            </div>

            <!-- Step 2 -->
            <div class="space-y-1.5">
              <Label>2. Customize the template</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                @click="openGuide">
                <BookOpen class="w-4 h-4 mr-1.5" />
                Read the step-by-step guide
                <ExternalLink class="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>

            <!-- Step 3 -->
            <div class="space-y-1.5">
              <Label>3. Upload your file</Label>
              <FileDropzone
                v-model="createFile"
                accept=".pptx"
                hint=".pptx files only" />
            </div>

            <p
              v-if="createError"
              class="text-sm text-destructive">{{ createError }}</p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              @click="showCreateModal = false">
              Cancel
            </Button>
            <Button
              :disabled="!createName.trim() || !createFile || creating || (showAccountSelect && selectedAccountIds.length === 0)"
              @click="handleCreate">
              <Loader2
                v-if="creating"
                class="w-4 h-4 mr-1.5 animate-spin" />
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        v-model:open="showDeleteDialog"
        title="Delete theme?"
        confirm-label="Delete"
        @confirm="handleDelete">
        "{{ themeToDelete?.name }}" will be deleted. Any report using it keeps working and is generated without a theme.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
